import React, { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import apiClient from "../../../services/api";
import { validateArticleUrl } from "../../../utils/url-validator";
import { useToast } from "../../../contexts/ToastContext";
import { articleRoleLabels } from "../shared/articleRoleLabels";

interface AddArticleToCallModalProps {
  isOpen: boolean;
  onClose: () => void;
  callId: string;
}

interface AddArticleFormData {
  url: string;
  articleRole: string;
}

/**
 * All article role options derived from the shared articleRoleLabels map
 * (single source of truth) so new ArticleType enum values appear automatically.
 */
const ARTICLE_ROLE_OPTIONS: { value: string; label: string }[] = Object.entries(
  articleRoleLabels
).map(([value, label]) => ({ value, label }));

/**
 * Modal for adding an article to a grant call by URL.
 * Calls POST /admin/grants/calls/:callId/articles
 * @see docs/specifications/2026-03-02-admin-article-management-on-call-details.md
 */
const AddArticleToCallModal: React.FC<AddArticleToCallModalProps> = ({
  isOpen,
  onClose,
  callId,
}) => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const firstInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState<AddArticleFormData>({
    url: "",
    articleRole: "",
  });
  const [urlError, setUrlError] = useState<string | null>(null);
  const [globalError, setGlobalError] = useState<string | null>(null);

  const addMutation = useMutation({
    mutationFn: async (data: AddArticleFormData) => {
      const { data: response } = await apiClient.post(
        `/admin/grants/calls/${callId}/articles`,
        { url: data.url, articleRole: data.articleRole }
      );
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["grants-call", callId] });
      onClose();
      addToast({ message: "Artykuł został dodany", type: "success" });
    },
    onError: (error: AxiosError<{ message?: string }>) => {
      const status = error?.response?.status;
      const message = error?.response?.data?.message;

      if (status === 400) {
        if (
          message?.includes("already") ||
          message?.toLowerCase().includes("powiązany")
        ) {
          setGlobalError("Artykuł jest już powiązany z tym naborem.");
        } else if (
          message?.toLowerCase().includes("url") ||
          message?.toLowerCase().includes("invalid")
        ) {
          setUrlError("Nieprawidłowy URL. Sprawdź format i protokół.");
        } else {
          setGlobalError(message ?? "Nieprawidłowe dane. Sprawdź URL.");
        }
      } else if (status === 404) {
        setGlobalError("Nabór nie został znaleziony.");
      } else if (status === 403) {
        setGlobalError("Brak uprawnień do wykonania tej operacji.");
      } else if (status && status >= 500) {
        setGlobalError("Błąd serwera. Spróbuj ponownie później.");
      } else if (!error?.response) {
        setGlobalError("Błąd połączenia. Sprawdź połączenie z internetem.");
      } else {
        setGlobalError("Wystąpił nieoczekiwany błąd. Spróbuj ponownie.");
      }
    },
  });

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({ url: "", articleRole: "" });
      setUrlError(null);
      setGlobalError(null);
      addMutation.reset();
    }
  // Reset form state only when modal visibility changes, not on every mutation state change
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-focus URL input when modal opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => firstInputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Handle ESC key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !addMutation.isPending) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
    }
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose, addMutation.isPending]);

  const validateUrl = (url: string): boolean => {
    const result = validateArticleUrl(url);
    if (!result.isValid) {
      setUrlError(result.error ?? "Nieprawidłowy URL.");
      return false;
    }
    setUrlError(null);
    return true;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setGlobalError(null);

    if (!validateUrl(formData.url)) {
      return;
    }

    addMutation.mutate(formData);
  };

  const handleClose = () => {
    if (!addMutation.isPending) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const isLoading = addMutation.isPending;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-article-modal-title"
    >
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 my-8">
        {/* Header */}
        <div className="px-6 py-4 border-b border-neutral-200">
          <h2
            id="add-article-modal-title"
            className="text-lg font-medium text-neutral-900"
          >
            Dodaj artykuł do naboru
          </h2>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          {globalError && (
            <div
              className="p-3 bg-red-50 border border-red-200 rounded-md"
              role="alert"
            >
              <p className="text-sm text-red-700">{globalError}</p>
            </div>
          )}

          {/* URL field */}
          <div>
            <label
              htmlFor="article-url"
              className="block text-sm font-medium text-neutral-700 mb-1"
            >
              URL artykułu <span className="text-red-600">*</span>
            </label>
            <input
              ref={firstInputRef}
              id="article-url"
              type="url"
              value={formData.url}
              onChange={(e) => {
                setFormData({ ...formData, url: e.target.value });
                setUrlError(null);
              }}
              onBlur={() => {
                if (formData.url.trim()) {
                  validateUrl(formData.url);
                }
              }}
              className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                urlError
                  ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                  : "border-neutral-300"
              }`}
              placeholder="https://example.com/artykul"
              required
              disabled={isLoading}
              aria-invalid={!!urlError}
              aria-describedby={urlError ? "url-error" : "url-hint"}
            />
            {urlError ? (
              <p id="url-error" className="mt-1 text-xs text-red-600" role="alert">
                {urlError}
              </p>
            ) : (
              <p id="url-hint" className="mt-1 text-xs text-neutral-500">
                URL musi zaczynać się od http:// lub https://.
                Tytuł zostanie zaktualizowany automatycznie po przetworzeniu artykułu.
              </p>
            )}
          </div>

          {/* Article role dropdown */}
          <div>
            <label
              htmlFor="article-role"
              className="block text-sm font-medium text-neutral-700 mb-1"
            >
              Charakter artykułu
            </label>
            <select
              id="article-role"
              value={formData.articleRole}
              onChange={(e) =>
                setFormData({ ...formData, articleRole: e.target.value })
              }
              className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              disabled={isLoading}
            >
              <option value="">-- wybierz charakter --</option>
              {ARTICLE_ROLE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-neutral-200 flex justify-end gap-3">
          <button
            type="button"
            onClick={handleClose}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-neutral-700 bg-neutral-100 rounded-md hover:bg-neutral-200 disabled:opacity-50"
          >
            Anuluj
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isLoading || !formData.url.trim() || !formData.articleRole}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
          >
            {isLoading && (
              <span
                className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"
                aria-hidden="true"
              />
            )}
            {isLoading ? "Dodawanie..." : "Dodaj artykuł"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddArticleToCallModal;
